import type Phaser from 'phaser';
import { SoundSettings } from './SoundSettings';
import { ASSETS } from '../config/assets';

const TILE_PLACE_URLS = ASSETS.gameAudio.TILE_PLACE;
const PASS_URLS = ASSETS.gameAudio.PASS;

// ── Phaser WebAudio gameplay bank ───────────────────────────────────────
// GameScene preloads every entry in GAMEPLAY_SOUND_BANK and registers its
// sound manager in create() / unregisters in cleanup(). Playback tries the
// Phaser path first (WebAudio: low latency, automatic iOS gesture unlock);
// if the manager isn't registered or the key isn't decoded, we fall back to
// the original HTMLAudioElement path — one engine fires per event, never both.
const KEYS = {
  TILE_PLACE:       ['sfx-tile-place-1', 'sfx-tile-place-2', 'sfx-tile-place-3'],
  TILE_SELECT:      'sfx-tile-select',
  TURN_INDICATOR:   'sfx-turn-indicator',
  PASS:             ['sfx-pass-1', 'sfx-pass-2'],
  GAME_RESULT_WIN:  'sfx-game-result-win',
  GAME_RESULT_LOSE: 'sfx-game-result-lose',
  SESSION_END_WIN:  'sfx-session-end-win',
  SESSION_END_LOSE: 'sfx-session-end-lose',
} as const;

/** Every gameplay sound GameScene must preload: [phaser key, asset url]. */
export const GAMEPLAY_SOUND_BANK: ReadonlyArray<readonly [string, string]> = [
  [KEYS.TILE_PLACE[0],   TILE_PLACE_URLS[0]],
  [KEYS.TILE_PLACE[1],   TILE_PLACE_URLS[1]],
  [KEYS.TILE_PLACE[2],   TILE_PLACE_URLS[2]],
  [KEYS.TILE_SELECT,     ASSETS.gameAudio.TILE_SELECT],
  [KEYS.TURN_INDICATOR,  ASSETS.gameAudio.TURN_INDICATOR],
  [KEYS.PASS[0],         PASS_URLS[0]],
  [KEYS.PASS[1],         PASS_URLS[1]],
  [KEYS.GAME_RESULT_WIN,  ASSETS.gameAudio.GAME_RESULT_WIN],
  [KEYS.GAME_RESULT_LOSE, ASSETS.gameAudio.GAME_RESULT_LOSE],
  [KEYS.SESSION_END_WIN,  ASSETS.gameAudio.SESSION_END_WIN],
  [KEYS.SESSION_END_LOSE, ASSETS.gameAudio.SESSION_END_LOSE],
];

let _phaserSound: Phaser.Sound.BaseSoundManager | null = null;

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// No-repeat round-robin for tile placement variations.
let _lastTilePlaceIdx = -1;
function nextTilePlaceIdx(): number {
  let idx = Math.floor(Math.random() * KEYS.TILE_PLACE.length);
  if (idx === _lastTilePlaceIdx) idx = (idx + 1) % KEYS.TILE_PLACE.length;
  _lastTilePlaceIdx = idx;
  return idx;
}

/**
 * Try the Phaser WebAudio path. Returns true if the sound was started —
 * callers must NOT run the HTMLAudio fallback in that case.
 * Assumes the caller already checked SoundSettings.getSoundEnabled().
 */
function playPhaser(key: string, volumeScale = 1): boolean {
  if (!_phaserSound) return false;
  if (!_phaserSound.game.cache.audio.exists(key)) return false;
  const volume = Math.max(0, Math.min(1, SoundSettings.getEffectsVolume() * volumeScale));
  return _phaserSound.play(key, { volume });
}

/** Original HTMLAudioElement path — kept as the per-sound fallback. */
function playHtml(url: string, volumeScale = 1): void {
  const audio = new Audio(url);
  audio.volume = Math.max(0, Math.min(1, SoundSettings.getEffectsVolume() * volumeScale));
  audio.play().catch(() => { /* blocked by autoplay policy or missing file — ignore */ });
}

function play(phaserKey: string, htmlUrl: string, volumeScale = 1): void {
  if (!SoundSettings.getSoundEnabled()) return;
  if (playPhaser(phaserKey, volumeScale)) return;
  playHtml(htmlUrl, volumeScale);
}

export const GameSoundManager = {
  /** Called by GameScene.create(). */
  registerPhaserSound(sound: Phaser.Sound.BaseSoundManager): void {
    _phaserSound = sound;
  },

  /**
   * Called by GameScene.cleanup(). The identity guard means a stale scene's
   * teardown can never clear a newer scene's registration.
   */
  unregisterPhaserSound(sound?: Phaser.Sound.BaseSoundManager): void {
    if (sound === undefined || _phaserSound === sound) _phaserSound = null;
  },

  playTileSelect(): void {
    play(KEYS.TILE_SELECT, ASSETS.gameAudio.TILE_SELECT);
  },

  playTilePlace(): void {
    const idx = nextTilePlaceIdx();
    play(KEYS.TILE_PLACE[idx], TILE_PLACE_URLS[idx]);
  },

  playPass(): void {
    const idx = Math.floor(Math.random() * KEYS.PASS.length);
    play(KEYS.PASS[idx], PASS_URLS[idx]);
  },

  playTurnIndicator(): void {
    play(KEYS.TURN_INDICATOR, ASSETS.gameAudio.TURN_INDICATOR, 0.8);
  },

  playTimerTick(): void {
    play(KEYS.TILE_SELECT, ASSETS.gameAudio.TILE_SELECT, 0.35);
  },

  playGameResult(won: boolean): void {
    if (won) play(KEYS.GAME_RESULT_WIN, ASSETS.gameAudio.GAME_RESULT_WIN);
    else     play(KEYS.GAME_RESULT_LOSE, ASSETS.gameAudio.GAME_RESULT_LOSE);
  },

  playSessionEnd(won: boolean): void {
    if (won) play(KEYS.SESSION_END_WIN, ASSETS.gameAudio.SESSION_END_WIN);
    else     play(KEYS.SESSION_END_LOSE, ASSETS.gameAudio.SESSION_END_LOSE);
  },
};
